import { useEffect, useState } from 'react';
import { api, type PackageView } from '../api.ts';
import { limit, message } from './Platform.tsx';

/** Blank is unlimited. Zero is a deliberate "none", and the two are different. */
const toLimit = (value: string): number | null =>
  value.trim() === '' ? null : Number(value.trim());

const fromLimit = (value: number | null): string => (value === null ? '' : String(value));

interface Draft {
  name: string;
  description: string;
  maxTenants: string;
  maxScansPerTenantPeriod: string;
  maxSchedules: string;
  maxSeats: string;
  frameworks: string[] | null;
}

const EMPTY: Draft = {
  name: '',
  description: '',
  maxTenants: '',
  maxScansPerTenantPeriod: '',
  maxSchedules: '',
  maxSeats: '',
  frameworks: null,
};

/**
 * The products we sell.
 *
 * The sentence that matters is on the editor and is repeated by the API in its
 * response: **editing a package changes every client already on it.** A widened
 * package widens them; a narrowed one narrows them, and a client the narrowing
 * puts over its limits stops being assessed until somebody fixes it.
 *
 * Which is why the status line after a save is not "Saved." — the API answers
 * with the clients it applied to and names the ones it has just stopped. That
 * sentence is rendered verbatim rather than summarised here: it is the only
 * warning anybody gets, and the person who typed the smaller number is the only
 * one who can undo it.
 */
export const Packages = () => {
  const [rows, setRows] = useState<PackageView[] | null>(null);
  const [catalogue, setCatalogue] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);

  const load = () => {
    void api
      .platformPackages()
      .then((response) => {
        setRows(response.packages);
        setCatalogue(response.frameworks);
      })
      .catch((cause: unknown) => setError(message(cause)));
  };

  useEffect(load, []);

  const body = () => ({
    name: draft.name.trim(),
    description: draft.description.trim() === '' ? null : draft.description.trim(),
    frameworks: draft.frameworks,
    maxTenants: toLimit(draft.maxTenants),
    maxScansPerTenantPeriod: toLimit(draft.maxScansPerTenantPeriod),
    maxSchedules: toLimit(draft.maxSchedules),
    maxSeats: toLimit(draft.maxSeats),
  });

  const save = async () => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      if (editing === null) {
        await api.createPackage(body());
        setStatus('Created.');
      } else {
        const response = await api.updatePackage(editing, body());
        setStatus(response.note);
      }
      setDraft(EMPTY);
      setEditing(null);
      load();
    } catch (cause) {
      setError(message(cause));
    } finally {
      setBusy(false);
    }
  };

  const edit = (entry: PackageView) => {
    setEditing(entry.id);
    setDraft({
      name: entry.name,
      description: entry.description ?? '',
      maxTenants: fromLimit(entry.maxTenants),
      maxScansPerTenantPeriod: fromLimit(entry.maxScansPerTenantPeriod),
      maxSchedules: fromLimit(entry.maxSchedules),
      maxSeats: fromLimit(entry.maxSeats),
      frameworks: entry.frameworks,
    });
  };

  const toggleFramework = (id: string) => {
    setDraft((current) => {
      if (current.frameworks === null) return { ...current, frameworks: [id] };
      const next = current.frameworks.includes(id)
        ? current.frameworks.filter((entry) => entry !== id)
        : [...current.frameworks, id];
      return { ...current, frameworks: next.length === 0 ? null : next };
    });
  };

  if (error !== null && rows === null) return <p className="error-banner">{error}</p>;
  if (rows === null) return <p className="note">Loading packages…</p>;

  return (
    <section className="pf-stack">
      {error !== null && <p className="error-banner">{error}</p>}

      <div className="card">
        <div className="card-head">
          <h2>Packages</h2>
        </div>
        <div className="card-body">
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Package</th>
                  <th>Tenants</th>
                  <th>Scans/tenant/month</th>
                  <th>Scheduled</th>
                  <th>Seats</th>
                  <th>Frameworks</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((entry) => (
                  <tr key={entry.id} className={entry.retiredAt !== null ? 'pf-retired' : undefined}>
                    <td>
                      {entry.name}
                      {entry.retiredAt !== null && <span className="hint"> retired</span>}
                    </td>
                    <td>{limit(entry.maxTenants)}</td>
                    <td>{limit(entry.maxScansPerTenantPeriod)}</td>
                    <td>{limit(entry.maxSchedules)}</td>
                    <td>{limit(entry.maxSeats)}</td>
                    <td>{entry.frameworks?.join(', ') ?? 'All'}</td>
                    <td>
                      <button type="button" className="link" onClick={() => edit(entry)}>
                        Edit
                      </button>{' '}
                      <button
                        type="button"
                        className="link"
                        disabled={busy}
                        onClick={() =>
                          void api
                            .retirePackage(entry.id, entry.retiredAt === null)
                            .then(load)
                            .catch((cause: unknown) => setError(message(cause)))
                        }
                      >
                        {entry.retiredAt === null ? 'Retire' : 'Reinstate'}
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <span className="note">
                        No packages yet. Until one is assigned, every client is unlimited.
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>{editing === null ? 'New package' : 'Edit package'}</h2>
        </div>
        <div className="card-body">
          <p className="lead">
            Blank is unlimited; <strong>0 is a deliberate none</strong>, and the two are different.
          </p>
          {editing !== null && (
            <div className="callout callout-warn">
              <span className="callout-title">This applies to every client on this package</span>
              Saving rewrites what they are allowed, immediately. A client the new numbers put over
              its limits stops being assessed until the package or its estate changes — you will be
              told which, by name, when you save.
            </div>
          )}

          <div className="td-fields">
            <div className="field">
              <label htmlFor="pk-name">Name</label>
              <input
                id="pk-name"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="pk-desc">Description</label>
              <input
                id="pk-desc"
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="pk-tenants">Connected tenants</label>
              <input
                id="pk-tenants"
                type="number"
                min={0}
                value={draft.maxTenants}
                placeholder="unlimited"
                onChange={(event) => setDraft({ ...draft, maxTenants: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="pk-scans">Assessments per tenant per month</label>
              <input
                id="pk-scans"
                type="number"
                min={0}
                value={draft.maxScansPerTenantPeriod}
                placeholder="unlimited"
                onChange={(event) =>
                  setDraft({ ...draft, maxScansPerTenantPeriod: event.target.value })
                }
              />
            </div>
            <div className="field">
              <label htmlFor="pk-schedules">Tenants with scheduled assessment</label>
              <input
                id="pk-schedules"
                type="number"
                min={0}
                value={draft.maxSchedules}
                placeholder="unlimited"
                onChange={(event) => setDraft({ ...draft, maxSchedules: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="pk-seats">Operator seats</label>
              <input
                id="pk-seats"
                type="number"
                min={0}
                value={draft.maxSeats}
                placeholder="unlimited"
                onChange={(event) => setDraft({ ...draft, maxSeats: event.target.value })}
              />
            </div>
          </div>

          <fieldset className="td-frameworks">
            <legend className="hint">
              Frameworks. None ticked means every framework, including any added later.
            </legend>
            {catalogue.map((id) => (
              <div className="field td-check" key={id}>
                <label htmlFor={`pk-fw-${id}`}>
                  <input
                    id={`pk-fw-${id}`}
                    type="checkbox"
                    checked={draft.frameworks?.includes(id) ?? false}
                    onChange={() => toggleFramework(id)}
                  />
                  {id}
                </label>
              </div>
            ))}
          </fieldset>

          <div className="toolbar">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || draft.name.trim() === ''}
              onClick={() => void save()}
            >
              {editing === null ? 'Create package' : 'Save package'}
            </button>
            {editing !== null && (
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setEditing(null);
                  setDraft(EMPTY);
                }}
              >
                Cancel
              </button>
            )}
            <span className="status-inline" role="status">
              {status}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
